import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "../../../components/common/NotificationProvider";
import toast from 'react-hot-toast';
import WarningPopup from "../../../components/WarningPopup";
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Save,
  ArrowLeft,
  Search,
  Calendar,
  Clock,
  Eye,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  User,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Upload
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { formatDate } from "../../../utils/stringUtils";
import { leaveTypeService } from "../../../api/crm/config/configService";
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import { RotateCcw } from "lucide-react";
import BulkUploadModal from '../../../components/common/BulkUploadModal';
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import LeaveBalanceImportModal from './LeaveBalanceImportModal';
import useUrlTableState from '../../../hooks/useUrlTableState';

const LeavePolicies = ({ onBack, userRole, initialMode = "list", id, onClose }) => {
  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 20,
    search: '',
    eligibleFor: 'all',
    status: 'all',
    startDate: '',
    endDate: ''
  }), []);

  const {
    filters: policyFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updatePolicyFilter,
    updatePage: updatePolicyPage,
    updateLimit: updatePolicyLimit,
    resetFilters: resetPolicyFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const filterEligibleFor = policyFilters.eligibleFor || 'all';
  const setFilterEligibleFor = useCallback((eligibleFor) => {
    updatePolicyFilter({ eligibleFor });
  }, [updatePolicyFilter]);

  const filterStatus = policyFilters.status || 'all';
  const setFilterStatus = useCallback((status) => {
    updatePolicyFilter({ status });
  }, [updatePolicyFilter]);

  const filterStartDate = policyFilters.startDate || '';
  const filterEndDate = policyFilters.endDate || '';

  const setDateRange = useCallback((range) => {
    updatePolicyFilter({ startDate: range.startDate || '', endDate: range.endDate || '' });
  }, [updatePolicyFilter]);

  const setFilterStartDate = useCallback((startDate) => {
    updatePolicyFilter({ startDate });
  }, [updatePolicyFilter]);

  const setFilterEndDate = useCallback((endDate) => {
    updatePolicyFilter({ endDate });
  }, [updatePolicyFilter]);

  const currentPagePolicies = policyFilters.page;
  const setCurrentPagePolicies = useCallback((page) => {
    updatePolicyPage(page);
  }, [updatePolicyPage]);

  const itemsPerPagePolicies = policyFilters.limit;
  const setItemsPerPagePolicies = useCallback((limit) => {
    updatePolicyLimit(limit);
  }, [updatePolicyLimit]);

  const [policies, setPolicies] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);
  const [isSearchingPolicies, setIsSearchingPolicies] = useState(false);
  const [showPolicyForm, setShowPolicyForm] = useState(initialMode === "create");
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showPolicyDetails, setShowPolicyDetails] = useState(initialMode === "view");
  const [selectedPolicyDetails, setSelectedPolicyDetails] = useState(null);

  // Export & Selection State
  const [selectedPolicyRows, setSelectedPolicyRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showBalanceImportModal, setShowBalanceImportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  // Mutations
  const deletePolicyMutation = useMutation({
    mutationFn: async (id) => {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/leave-policies/leave/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Authorization": token ? `Bearer ${token}` : ''
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to delete policy");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leave-policies"] });
    },
  });

  const [formData, setFormData] = useState({
    name: "",
    type: "",
    description: "",
    entitlement: "",
    applicableRoles: "All",
    isActive: true,
    leaveTypeId: "",
    isUnpaid: false,
    // Accrual Settings
    accrualFrequency: "monthly",
    accrualDay: "last",
    accrualRatePerCycle: "",
    // Application Rules
    allowHalfDay: true,
    requireReason: true,
    documentProofThreshold: 0,
    enforceRestrictedHolidays: false,
    // Advance Notice Rules
    advanceNoticeRules: [],
    // Year-End Settings
    yearEndAction: "carry_forward",
    maxCarryForwardDays: "",
    // Sandwich Rule Settings
    sandwichRule: {
      enabled: false,
      includeWeekends: true,
      includeHolidays: true,
      requireFullDayOnBothSides: true,
      treatAbsentAsLeave: true,
      excludedLeaveTypes: []
    }
  });
  const [dynamicLeaveTypes, setDynamicLeaveTypes] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  // Leave requests state (moved to top to avoid initialization issues)
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTermRequests, setSearchTermRequests] = useState("");
  const [filterStatusRequests, setFilterStatusRequests] = useState("all");
  const [filterTypeRequests, setFilterTypeRequests] = useState("all");
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsRequests, setShowDetailsRequests] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Pagination state for leave requests
  const [currentPageRequests, setCurrentPageRequests] = useState(1);
  const [totalPagesRequests, setTotalPagesRequests] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const [itemsPerPageRequests, setItemsPerPageRequests] = useState(10);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [formDataRequests, setFormDataRequests] = useState({
    leaveType: "",
    startDate: "",
    endDate: "",
    reason: "",
    description: "",
    emergencyContact: "",
    handoverNotes: "",
    employeeId: "",
    employeeName: "",
    employeeEmail: "",
  });

  // Form validation state for leave requests
  const [formErrorsRequests, setFormErrorsRequests] = useState({});
  const [isValidatingRequests, setIsValidatingRequests] = useState(false);

  // Debounce search term - useUrlTableState handles this internally

  // Handle deep-linking to specific policy
  useEffect(() => {
    if (!id) return;

    const fetchPolicyById = async () => {
      setIsLoadingPolicies(true);
      try {
        const token = localStorage.getItem("auth_token");
        const url = `${process.env.REACT_APP_API_BASE_URL}/leave-policies/leave/${id}`;
        const response = await fetch(url, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });
        const result = await response.json();
        if ((result.status === "success" || result.responseCode === 200) && result.data) {
          if (initialMode === "view") {
            setSelectedPolicyDetails(result.data);
            setShowPolicyDetails(true);
          } else if (initialMode === "edit") {
            const policy = result.data;
            let applicableRole = "All";
            if (policy.applicableRoles) {
              if (Array.isArray(policy.applicableRoles) && policy.applicableRoles.length > 0) {
                applicableRole = policy.applicableRoles[0];
              } else if (typeof policy.applicableRoles === 'string') {
                applicableRole = policy.applicableRoles;
              }
            }
            setFormData({
              name: policy.name || "",
              type: policy.type || "",
              description: policy.description || "",
              leaveTypeId: policy.leaveTypeId || "",
              entitlement: policy.entitlement ? policy.entitlement.toString() : "",
              applicableRoles: applicableRole,
              isActive: policy.isActive !== undefined ? policy.isActive : true,
              isUnpaid: policy.isUnpaid || false,
              accrualFrequency: policy.accrualFrequency || "monthly",
              accrualDay: policy.accrualDay || "last",
              accrualRatePerCycle: policy.accrualRatePerCycle ? policy.accrualRatePerCycle.toString() : "",
              allowHalfDay: policy.allowHalfDay !== undefined ? policy.allowHalfDay : true,
              requireReason: policy.requireReason !== undefined ? policy.requireReason : true,
              documentProofThreshold: policy.documentProofThreshold || 0,
              enforceRestrictedHolidays: policy.enforceRestrictedHolidays || false,
              advanceNoticeRules: policy.advanceNoticeRules || [],
              yearEndAction: policy.yearEndAction || "carry_forward",
              maxCarryForwardDays: policy.maxCarryForwardDays ? policy.maxCarryForwardDays.toString() : "",
            });
            setEditingPolicy(policy);
            setShowPolicyForm(true);
          }
        }
      } catch (error) {
      } finally {
        setIsLoadingPolicies(false);
      }
    };

    fetchPolicyById();
  }, [id, initialMode]);

  const fetchDynamicLeaveTypes = async () => {
    try {
      const response = await leaveTypeService.getDropdown();
      if (response && response.data) {
        setDynamicLeaveTypes(response.data);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchDynamicLeaveTypes();
  }, []);

  useEffect(() => {
    const totalPolicies = filteredPolicies?.length || 0;
    const totalPages = Math.max(1, Math.ceil(totalPolicies / itemsPerPagePolicies) || 1);
    if (currentPagePolicies > totalPages) {
      setCurrentPagePolicies(totalPages);
    }
  }, [filteredPolicies, itemsPerPagePolicies, currentPagePolicies]);

  useEffect(() => {
    loadPolicies();
  }, []);

  // Load policies with pagination - useUrlTableState handles debouncing
  useEffect(() => {
    loadPolicies(searchTerm, filterEligibleFor, filterStatus, filterStartDate, filterEndDate);
  }, [searchTerm, filterEligibleFor, filterStatus, currentPagePolicies, itemsPerPagePolicies, filterStartDate, filterEndDate]);

  // Reset selection when filters, search, pagination change
  useEffect(() => {
    setSelectedPolicyRows(new Set());
  }, [searchTerm, filterEligibleFor, filterStatus, currentPagePolicies, itemsPerPagePolicies, filterStartDate, filterEndDate]);

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Build base export filters
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm || undefined,
      eligibleFor: filterEligibleFor !== 'all' ? filterEligibleFor : undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      startDate: filterStartDate || undefined,
      endDate: filterEndDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm, filterEligibleFor, filterStatus, filterStartDate, filterEndDate]);

  // Get current page policy IDs
  const currentPagePolicyIds = useMemo(
    () =>
      filteredPolicies
        .map((p) => {
          const raw = p?._id ?? p?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [filteredPolicies]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPagePolicyIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'leave-policies',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? currentPagePolicies,
            limit: overrideLimit ?? itemsPerPagePolicies,
            sortBy: sortField || 'name',
            sortOrder,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
            pdfFormat: format === 'pdf' ? 'a4' : undefined,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `leave-policies-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPagePolicies,
      itemsPerPagePolicies,
      sortField,
      sortOrder,
      currentPagePolicyIds,
    ]
  );

  // Handle export for selected policies with format selection
  const handleSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedPolicyRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected policies to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedPolicyRows, requestBackendExport]
  );

  // Handle scoped export from modal
  const handleScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPagePolicies,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPagePolicyIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      filteredPolicies.length,
      baseExportFilters,
      requestBackendExport,
      itemsPerPagePolicies,
      currentPagePolicyIds,
    ]
  );

  // Export click handler
  const handleExportClick = () => {
    if (selectedPolicyRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };


  // Reset to first page when filters change (but not when page changes)
  useEffect(() => {
    if (currentPageRequests !== 1) {
      setCurrentPageRequests(1);
    }
  }, [searchTermRequests, filterStatusRequests, filterTypeRequests, itemsPerPageRequests]);

  // Leave request functions (copied from LeaveRequests.jsx)
  const loadLeaveRequests = useCallback(async () => {
    try {
      setLoadingRequests(true);

      // Get all employees with leaveHistory data
      const employeesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/leaverequest`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!employeesResponse.ok) {
        throw new Error(`HTTP error! status: ${employeesResponse.status}`);
      }

      const employeesResult = await employeesResponse.json();

      if (employeesResult.success) {
        // Extract leave requests from employee data
        let allLeaveRequests = [];

        // Process each employee's leaveHistory
        employeesResult.data.forEach(employee => {
          // Only process employees who have leave requests
          if (employee.leaveHistory && employee.leaveHistory.length > 0) {
            employee.leaveHistory.forEach((leaveRequest) => {
              // Find the corresponding leave type info for this request
              let remainingBalance = 0;
              let totalAllowed = 0;
              let usedDays = 0;

              if (employee.leaveInfo && employee.leaveInfo.leaveTypes && employee.leaveInfo.leaveTypes.length > 0) {
                const matchingLeaveType = employee.leaveInfo.leaveTypes.find(lt => lt.type === leaveRequest.leaveType);
                if (matchingLeaveType) {
                  remainingBalance = matchingLeaveType.remaining || 0;
                  totalAllowed = matchingLeaveType.total || 0;
                  usedDays = matchingLeaveType.used || 0;
                }
              }

              allLeaveRequests.push({
                id: leaveRequest.leaveRequestId,
                employeeId: employee.id,
                employeeName: employee.name,
                department: employee.department || "Unknown",
                leaveType: leaveRequest.leaveType,
                startDate: leaveRequest.startDate?.split('T')[0] || new Date(leaveRequest.startDate).toISOString().split('T')[0],
                endDate: leaveRequest.endDate?.split('T')[0] || new Date(leaveRequest.endDate).toISOString().split('T')[0],
                totalDays: leaveRequest.totalDays,
                reason: leaveRequest.reason,
                description: leaveRequest.description || leaveRequest.reason,
                emergencyContact: leaveRequest.emergencyContact || "Not provided",
                handoverNotes: leaveRequest.handoverNotes || "Not provided",
                status: leaveRequest.status || "pending",
                appliedDate: leaveRequest.appliedDate?.split('T')[0] || new Date(leaveRequest.appliedDate).toISOString().split('T')[0],
                approvedBy: leaveRequest.approvedBy || null,
                approvedDate: leaveRequest.approvedDate ? (leaveRequest.approvedDate.split ? leaveRequest.approvedDate.split('T')[0] : new Date(leaveRequest.approvedDate).toISOString().split('T')[0]) : null,
                comments: leaveRequest.comments || null,
                remainingBalance: remainingBalance,
                totalAllowed: totalAllowed,
                usedDays: usedDays
              });
            });
          }
        });

        // Sort requests by applied date (newest first)
        allLeaveRequests.sort((a, b) => {
          const dateA = new Date(a.appliedDate);
          const dateB = new Date(b.appliedDate);
          return dateB - dateA; // Descending order (newest first)
        });

        // Apply client-side filtering
        let filteredRequests = allLeaveRequests;

        // Apply search filter
        if (searchTermRequests.trim()) {
          const searchLower = searchTermRequests.toLowerCase();
          filteredRequests = filteredRequests.filter(request =>
            request.employeeName.toLowerCase().includes(searchLower) ||
            request.employeeId.toLowerCase().includes(searchLower) ||
            request.leaveType.toLowerCase().includes(searchLower) ||
            request.reason.toLowerCase().includes(searchLower)
          );
        }

        // Apply status filter
        if (filterStatusRequests !== "all") {
          filteredRequests = filteredRequests.filter(request => request.status === filterStatusRequests);
        }

        // Apply leave type filter
        if (filterTypeRequests !== "all") {
          filteredRequests = filteredRequests.filter(request => request.leaveType === filterTypeRequests);
        }

        // Apply pagination
        const totalFilteredRequests = filteredRequests.length;
        const totalPages = Math.ceil(totalFilteredRequests / itemsPerPageRequests);
        const startIndex = (currentPageRequests - 1) * itemsPerPageRequests;
        const endIndex = startIndex + itemsPerPageRequests;
        const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

        setLeaveRequests(paginatedRequests);
        setTotalPagesRequests(totalPages);
        setTotalRequests(totalFilteredRequests);
      } else {
        showError("Failed to load employees: " + (employeesResult.responseMessage || employeesResult.message || "Unknown error"));
        setLeaveRequests([]);
      }
    } catch (error) {
      showError(`Failed to load leave requests: ${error.message}. Please check your connection and try again.`);
      setLeaveRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  }, [currentPageRequests, searchTermRequests, filterStatusRequests, filterTypeRequests, itemsPerPageRequests]);

  // Load data when page or filters change

  const loadEmployees = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/leaverequest`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setEmployees(result.data);
      } else {
        showError(
          "Failed to load employees: " + (result.message || "Unknown error")
        );
      }
    } catch (error) {
      showError(
        "Failed to load employees. Please check your connection and try again."
      );
      setEmployees([]);
    }
  };

  // Utility functions (copied from LeaveRequests.jsx)
  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Get sorted policies
  const getSortedPolicies = (policies) => {
    if (!sortField || !policies || policies.length === 0) {
      return policies;
    }

    // Safe accessor helper
    const getSafeValue = (obj, path) => {
      if (!obj) return '';
      if (!path) return obj;
      if (typeof path === 'function') return path(obj);
      return obj[path];
    };

    const sorted = [...policies].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;

        case 'type':
          aValue = (a.type || '').toLowerCase();
          bValue = (b.type || '').toLowerCase();
          break;

        case 'entitlement':
          aValue = Number(a.entitlement) || 0;
          bValue = Number(b.entitlement) || 0;
          break;

        case 'eligibleFor':
          const getRoleString = (roles) => {
            if (Array.isArray(roles)) return roles.join(', ');
            return roles || '';
          };
          aValue = getRoleString(a.applicableRoles).toLowerCase();
          bValue = getRoleString(b.applicableRoles).toLowerCase();
          break;

        case 'status':
          aValue = (a.isActive ? 'active' : 'inactive');
          bValue = (b.isActive ? 'active' : 'inactive');
          break;

        case 'createdAt':
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : (a.createdDate ? new Date(a.createdDate).getTime() : 0);
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : (b.createdDate ? new Date(b.createdDate).getTime() : 0);
          break;

        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Create comparable primitives if they are objects (though our cases above handle this)
      if (typeof aValue === 'object') aValue = JSON.stringify(aValue);
      if (typeof bValue === 'object') bValue = JSON.stringify(bValue);

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };


  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Pagination functions (copied from LeaveRequests.jsx)
  const handlePageChangeRequests = (newPage) => {
    if (newPage >= 1 && newPage <= totalPagesRequests) {
      setCurrentPageRequests(newPage);
    }
  };

  const handleItemsPerPageChangeRequests = (newItemsPerPage) => {
    setItemsPerPageRequests(newItemsPerPage);
    setCurrentPageRequests(1); // Reset to first page when changing items per page
  };

  const goToFirstPageRequests = () => handlePageChangeRequests(1);
  const goToLastPageRequests = () => handlePageChangeRequests(totalPagesRequests);
  const goToPreviousPageRequests = () => handlePageChangeRequests(currentPageRequests - 1);
  const goToNextPageRequests = () => handlePageChangeRequests(currentPageRequests + 1);

  // Generate page numbers for pagination
  const getPageNumbersRequests = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(1, currentPageRequests - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPagesRequests, startPage + maxVisiblePages - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Filter requests function
  const filterRequests = () => {
    // Since we're now using backend filtering, we just set the requests as filtered
    setFilteredRequests(leaveRequests);
  };

  const handlePolicyPageChange = (page) => {
    if (page >= 1) {
      setCurrentPagePolicies(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePolicyItemsPerPageChange = (limit) => {
    setItemsPerPagePolicies(limit);
  };

  const sortedPolicies = getSortedPolicies(filteredPolicies);
  const totalPolicies = sortedPolicies?.length || 0;
  const totalPolicyPages = Math.max(1, Math.ceil(totalPolicies / itemsPerPagePolicies) || 1);
  const currentPolicyPage = Math.min(currentPagePolicies, totalPolicyPages);
  const policyStartIndex = (currentPolicyPage - 1) * itemsPerPagePolicies;
  const visiblePolicies = sortedPolicies.slice(
    policyStartIndex,
    policyStartIndex + itemsPerPagePolicies
  );
  const hasPrevPolicyPage = currentPolicyPage > 1;
  const hasNextPolicyPage = currentPolicyPage < totalPolicyPages;
  const shouldShowPolicyPagination =
    !isLoadingPolicies &&
    !isSearchingPolicies;


  // --- Export & Selection Logic ---

  // clear selection on filter/page change
  useEffect(() => {
    setSelectedPolicyRows(new Set());
  }, [searchTerm, filterEligibleFor, filterStatus, currentPagePolicies, filterStartDate, filterEndDate]);

  const handlePolicyRowSelect = (id) => {
    const newSelected = new Set(selectedPolicyRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedPolicyRows(newSelected);
  };

  const handleSelectAllPolicies = () => {
    const currentPageIds = visiblePolicies.map(p => p.id);
    const selectedOnPage = currentPageIds.filter(id => selectedPolicyRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedPolicyRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedPolicyRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedPolicyRows(newSelected);
    }
  };

  const loadPolicies = async (search = "", eligibleFor = "all", status = "all", startDate = "", endDate = "") => {

    try {
      setIsLoadingPolicies(true);
      setIsSearchingPolicies(Boolean(search && search.trim()));

      const token = localStorage.getItem("auth_token");

      // Build query string with search and filter parameters
      const queryParams = new URLSearchParams();
      if (search && search.trim()) {
        queryParams.append("search", search.trim());
      }
      if (eligibleFor && eligibleFor !== "all") {
        queryParams.append("role", eligibleFor);
      }
      if (status && status !== "all") {
        queryParams.append("isActive", status === "active" ? "true" : "false");
      }
      if (startDate) {
        queryParams.append("startDate", startDate);
      }
      if (endDate) {
        queryParams.append("endDate", endDate);
      }
      const queryString = queryParams.toString();
      const url = `${process.env.REACT_APP_API_BASE_URL}/leave-policies/leave${queryString ? `?${queryString}` : ""}`;

      if (!token) {
        // Try without authentication first
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.status === "success") {
          setPolicies(result.data);
          setFilteredPolicies(result.data);
          return;
        }

        showError("Please login to view leave policies");
        setPolicies([]);
        setFilteredPolicies([]);
        return;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === "success" || result.responseCode === 200) {
        setPolicies(result.data);
        setFilteredPolicies(result.data);
      } else {
        showError(
          "Failed to load leave policies: " +
          (result.responseMessage || result.message)
        );
        setPolicies([]);
        setFilteredPolicies([]);
      }
    } catch (error) {
      showError(
        "Failed to load leave policies. Please check your connection and try again."
      );
      setPolicies([]); // Set empty array instead of mock data
      setFilteredPolicies([]);
    } finally {
      setIsLoadingPolicies(false);
      setIsSearchingPolicies(false);
    }
  };




  const roles = [
    "All",
    "Males",
    "Females",
  ];

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];


  // Validation functions
  const validateTextLength = (text, minLength, maxLength) => {
    if (!text || text.trim().length < minLength) {
      return `Must be at least ${minLength} characters`;
    }
    if (text.length > maxLength) {
      return `Must be no more than ${maxLength} characters`;
    }
    return null;
  };

  const validateNumber = (value, min, max, fieldName) => {
    if (!value || value === '') {
      return `${fieldName} is required`;
    }
    const numValue = parseInt(value);
    if (isNaN(numValue)) {
      return `${fieldName} must be a valid number`;
    }
    if (numValue < min) {
      return `${fieldName} must be at least ${min}`;
    }
    if (numValue > max) {
      return `${fieldName} must be no more than ${max}`;
    }
    return null;
  };

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "name":
        if (!value?.trim()) {
          error = "Policy name is required";
        } else if (value.length > 100) {
          error = "Policy name must be ≤ 100 characters";
        } else if (!/^[a-zA-Z0-9\s,]+$/.test(value)) {
          error = "Policy name can only contain alphanumeric characters, spaces, and commas";
        } else if (value.length < 3) {
          error = "Policy name must be at least 3 characters";
        }
        break;
      case "type":
        if (!value) {
          error = "Please select a leave type";
        }
        break;
      case "entitlement":
        error = validateNumber(value, 1, 180, "Entitlement");
        break;
      case "applicableRoles":
        if (!value || value === "") {
          error = "Please select who this policy is eligible for";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const validateForm = () => {
    const errors = {};
    const fieldsToValidate = ["name", "type", "entitlement", "applicableRoles"];

    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
      }
    });

    return errors;
  };

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Handle key down for number inputs (prevent negative signs and decimals)
  const handleNumberKeyDown = (e) => {
    // Prevent negative sign, decimal point, and 'e' or 'E' (scientific notation)
    if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;

    // Text name field: allow alphanumeric, spaces, and commas
    if (name === 'name') {
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '');
      if (processedValue.length > 100) processedValue = processedValue.slice(0, 100);
    }

    // Number field validation
    if (name === 'entitlement') {
      processedValue = value.replace(/\D/g, '');
      if (processedValue !== '') {
        const numValue = parseInt(processedValue);
        if (numValue > 180) processedValue = '180';
        if (numValue < 1 && processedValue !== '0') processedValue = '1';
      }
    }

    const newValue = type === "checkbox" ? checked : (processedValue !== undefined ? processedValue : value);

    setFormData((prev) => {
      const updatedData = { ...prev, [name]: newValue };

      // Handle leave type mapping
      if (name === 'type') {
        const selectedDynamicType = dynamicLeaveTypes.find(lt => lt.name === newValue || lt._id === newValue);
        if (selectedDynamicType) {
          updatedData.leaveTypeId = selectedDynamicType._id;
          updatedData.type = selectedDynamicType.name;
        } else {
          updatedData.leaveTypeId = "";
        }
      }

      // Auto-calculate Accrual Rate per Cycle
      if (name === 'entitlement' || name === 'accrualFrequency') {
        const ent = parseFloat(updatedData.entitlement) || 0;
        const freq = updatedData.accrualFrequency;
        
        let rate = 0;
        if (freq === 'monthly') rate = ent / 12;
        else if (freq === 'quarterly') rate = ent / 4;
        else if (freq === 'annually') rate = ent;
        else rate = 0;

        // Round to 4 decimal places for precision but clean display
        updatedData.accrualRatePerCycle = rate > 0 ? parseFloat(rate.toFixed(4)).toString() : "0";
      }

      return updatedData;
    });

    // Immediate field-level validation
    const error = validateField(name, newValue);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSandwichChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      sandwichRule: {
        ...(prev.sandwichRule || {}),
        [name]: checked
      }
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };


  const handleSubmitPolicy = async (e) => {
    e.preventDefault();

    // Validate form before submission
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Clear any existing errors
    setFormErrors({});

    try {
      const token = localStorage.getItem("auth_token");

      if (!token) {
        showError(
          "Please login to manage leave policies. You need to be logged in to create or update policies."
        );
        return;
      }

      const policyData = {
        ...formData,
        entitlement: parseInt(formData.entitlement),
        accrualRatePerCycle: parseFloat(formData.accrualRatePerCycle) || 0,
        documentProofThreshold: parseInt(formData.documentProofThreshold) || 0,
        enforceRestrictedHolidays: formData.enforceRestrictedHolidays,
        maxCarryForwardDays: parseInt(formData.maxCarryForwardDays) || 0,
        applicableRoles: Array.isArray(formData.applicableRoles) ? formData.applicableRoles : [formData.applicableRoles],
      };

      const url = editingPolicy
        ? `${process.env.REACT_APP_API_BASE_URL}/leave-policies/leave/${editingPolicy.id}` // Use leave endpoint for updates
        : `${process.env.REACT_APP_API_BASE_URL}/leave-policies/leave`; // Use leave endpoint for creation

      const method = editingPolicy ? "PUT" : "POST";

      // For test endpoints, don't send auth header
      const headers = {
        "Content-Type": "application/json",
      };

      // Only add auth header for non-test endpoints
      if (!url.includes("/test")) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: method,
        headers: headers,
        body: JSON.stringify(policyData),
      });

      if (!response.ok) {
        let errorResult;
        try {
          errorResult = await response.json();
        } catch (e) {
          errorResult = {
            message: `HTTP ${response.status}: ${response.statusText}`,
          };
        }

        const errorMessage = errorResult.responseMessage || errorResult.message || "Unknown error";

        // Check if error is related to leave type already existing
        if (errorMessage.includes("leave type") && errorMessage.includes("already exists")) {
          setFormErrors(prev => ({
            ...prev,
            type: errorMessage
          }));
          showError(errorMessage);
          return;
        }

        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorMessage}`
        );
      }

      const result = await response.json();

      if (result.status === "success" || result.responseCode === 200 || result.responseCode === 201) {
        showSuccess(`Policy ${editingPolicy ? "updated" : "created"} successfully!`);
        await loadPolicies();
        resetForm();
        if (onClose) onClose();
      } else {
        showError(
          "Failed to submit leave policy: " +
          (result.responseMessage || result.message || "Unknown error")
        );
      }
    } catch (error) {
      // Check if error message contains leave type validation
      if (error.message && error.message.includes("leave type") && error.message.includes("already exists")) {
        setFormErrors(prev => ({
          ...prev,
          type: error.message.split("message: ")[1] || error.message
        }));
        showError(error.message.split("message: ")[1] || error.message);
      } else {
        showError("Failed to submit leave policy. Please try again.");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      type: "",
      description: "",
      entitlement: "",
      applicableRoles: "All",
      isActive: true,
      leaveTypeId: "",
      isUnpaid: false,
      accrualFrequency: "monthly",
      accrualDay: "last",
      accrualRatePerCycle: "",
      joiningWaitDays: 0,
      restrictNoticePeriod: false,
      maxConsecutiveDays: "",
      monthlyUsageCap: "",
      pastDayLimit: 0,
      allowHalfDay: true,
      requireReason: true,
      documentProofThreshold: 0,
      advanceNoticeRules: [],
      yearEndAction: "carry_forward",
      maxCarryForwardDays: "",
      sandwichRule: {
        enabled: false,
        includeWeekends: true,
        includeHolidays: true,
        requireFullDayOnBothSides: true,
        treatAbsentAsLeave: true,
        excludedLeaveTypes: []
      }
    });
    setFormErrors({});
    setShowPolicyForm(false);
    setEditingPolicy(null);
    if (onClose) onClose();
  };

  const openPolicyDetails = (policy) => {
    setSelectedPolicyDetails(policy);
    setShowPolicyDetails(true);
  };

  const closePolicyDetails = () => {
    setShowPolicyDetails(false);
    setSelectedPolicyDetails(null);
  };

  const handleEditPolicy = (policy) => {
    // Handle both array and string formats for backward compatibility
    let applicableRole = "All";
    if (policy.applicableRoles) {
      if (Array.isArray(policy.applicableRoles) && policy.applicableRoles.length > 0) {
        applicableRole = policy.applicableRoles[0];
      } else if (typeof policy.applicableRoles === 'string') {
        applicableRole = policy.applicableRoles;
      }
    }

    setFormData({
      name: policy.name || "",
      type: policy.type || "",
      description: policy.description || "",
      leaveTypeId: policy.leaveTypeId || "",
      entitlement: policy.entitlement ? policy.entitlement.toString() : "",
      applicableRoles: applicableRole,
      isActive: policy.isActive !== undefined ? policy.isActive : true,
      isUnpaid: policy.isUnpaid || false,
      accrualFrequency: policy.accrualFrequency || "monthly",
      accrualDay: policy.accrualDay || "last",
      accrualRatePerCycle: policy.accrualRatePerCycle ? policy.accrualRatePerCycle.toString() : "",
      joiningWaitDays: policy.joiningWaitDays || 0,
      restrictNoticePeriod: policy.restrictNoticePeriod || false,
      maxConsecutiveDays: policy.maxConsecutiveDays ? policy.maxConsecutiveDays.toString() : "",
      monthlyUsageCap: policy.monthlyUsageCap ? policy.monthlyUsageCap.toString() : "",
      pastDayLimit: policy.pastDayLimit || 0,
      allowHalfDay: policy.allowHalfDay !== undefined ? policy.allowHalfDay : true,
      requireReason: policy.requireReason !== undefined ? policy.requireReason : true,
      documentProofThreshold: policy.documentProofThreshold || 0,
      enforceRestrictedHolidays: policy.enforceRestrictedHolidays || false,
      advanceNoticeRules: policy.advanceNoticeRules || [],
      yearEndAction: policy.yearEndAction || "carry_forward",
      maxCarryForwardDays: policy.maxCarryForwardDays ? policy.maxCarryForwardDays.toString() : "",
      sandwichRule: policy.sandwichRule || {
        enabled: false,
        includeWeekends: true,
        includeHolidays: true,
        requireFullDayOnBothSides: true,
        treatAbsentAsLeave: true,
        excludedLeaveTypes: []
      }
    });
    setFormErrors({});
    setEditingPolicy(policy);
    setShowPolicyForm(true);
  };

  const handleDeletePolicy = (policyId) => {
    const policy = policies.find((p) => p.id === policyId);
    if (!policy) {
      showError("Policy not found!");
      return;
    }
    setPendingAction({ type: "delete", policyId, policy });
    setIsBulkDelete(false);
    setShowDeleteConfirm(true);
  };

  const handleBulkDeletePolicy = () => {
    if (selectedPolicyRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (isBulkDelete) {
      const idsToDelete = Array.from(selectedPolicyRows);
      let successCount = 0;
      let errorCount = 0;

      for (const id of idsToDelete) {
        try {
          await deletePolicyMutation.mutateAsync(id);
          successCount++;
        } catch (error) {
          errorCount++;
        }
      }

      if (successCount > 0) {
        showSuccess(`${successCount} policies deleted successfully!`);
        setSelectedPolicyRows(new Set());
        loadPolicies(searchTerm, filterEligibleFor, filterStatus);
      }
      if (errorCount > 0) {
        showError(`Failed to delete ${errorCount} policies.`);
      }
      setShowDeleteConfirm(false);
      setIsBulkDelete(false);
      return;
    }

    if (!pendingAction || pendingAction.type !== "delete") return;

    const { policyId, policy } = pendingAction;

    try {
      await deletePolicyMutation.mutateAsync(policyId);
      showSuccess(`✅ Policy "${policy.name}" has been deleted successfully!`);
      loadPolicies(debouncedSearchTerm, filterEligibleFor, filterStatus);
    } catch (error) {
      showError(`❌ Failed to delete policy: ${error.message}`);
    } finally {
      setShowDeleteConfirm(false);
      setPendingAction(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setPendingAction(null);
    setIsBulkDelete(false); // Reset bulk delete state
  };


  const togglePolicyStatus = (policyId) => {
    // Find the policy to get current status
    const policy = policies.find((p) => p.id === policyId);
    if (!policy) {
      showError("Policy not found!");
      return;
    }
    setPendingAction({ type: "toggle", policyId, policy });
    setShowToggleConfirm(true);
  };

  const confirmToggle = async () => {
    if (!pendingAction || pendingAction.type !== "toggle") return;

    const { policyId, policy } = pendingAction;

    try {
      // Call the test toggle API endpoint
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/leave-policies/leave/${policyId}/toggle`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === "success") {
        // Show success message
        showSuccess(
          `Policy "${policy.name}" has been ${!policy.isActive ? "activated" : "deactivated"
          } successfully!`
        );

        // Reload policies from API to get the updated status
        await loadPolicies(debouncedSearchTerm, filterEligibleFor, filterStatus);
      } else {
        throw new Error(result.message || "Failed to toggle policy status");
      }
    } catch (error) {
      showError(`❌ Failed to toggle policy status: ${error.message}`);
    } finally {
      setShowToggleConfirm(false);
      setPendingAction(null);
    }
  };

  const cancelToggle = () => {
    setShowToggleConfirm(false);
    setPendingAction(null);
  };



  // Real employee leave balances from database
  const [employeeBalances, setEmployeeBalances] = useState([]);
  const [loadingBalances, setLoadingBalances] = useState(false);

  // Load employee balances from database
  const loadEmployeeBalances = async () => {
    try {
      setLoadingBalances(true);
      // Fetch employees from regularization API (reusing existing endpoint)
      const employeesResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/regularization-test/employees`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!employeesResponse.ok) {
        throw new Error(`HTTP error! status: ${employeesResponse.status}`);
      }

      const employeesResult = await employeesResponse.json();

      if (employeesResult.success) {
        // Transform employees to balance format using actual leaveInfo data
        const transformedBalances = employeesResult.data.map((employee) => {
          // Extract leaveInfo from employee data
          const leaveInfo = employee.leaveInfo || {};
          const totalLeaves = leaveInfo.totalLeaves || 0;
          const usedLeaves = leaveInfo.usedLeaves || 0;
          const remainingLeaves = leaveInfo.remainingLeaves || totalLeaves;

          return {
            employeeId: employee.id,
            employeeName: employee.name,
            department: employee.department,
            email: employee.email,
            position: employee.position,
            leaveInfo: {
              totalLeaves: totalLeaves,
              usedLeaves: usedLeaves,
              remainingLeaves: remainingLeaves,
            },
          };
        });

        setEmployeeBalances(transformedBalances);
      } else {
        showError(
          "Failed to load employee balances: " +
          (employeesResult.message || "Unknown error")
        );
      }
    } catch (error) {
      showError(
        "Failed to load employee balances. Please check your connection and try again."
      );
      setEmployeeBalances([]);
    } finally {
      setLoadingBalances(false);
    }
  };


  if (showPolicyForm) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetForm}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">
              {editingPolicy ? "Edit Leave Policy" : "New Leave Policy"}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmitPolicy} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Policy Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Policy Name <span className="text-red-500">*</span> <span className="text-gray-500 text-xs">(3-100 characters)</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onKeyPress={handleTextKeyPress}
                onBlur={handleBlur}
                required
                maxLength={100}
                placeholder="e.g., Annual Leave Policy"
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formErrors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              />
              <div className="flex justify-between items-center mt-1">
                {formErrors.name ? (
                  <p className="text-sm text-red-600">{formErrors.name}</p>
                ) : (
                  <div></div>
                )}
                <span className="text-xs text-gray-500">
                  {formData.name.length}/100
                </span>
              </div>
            </div>

            {/* Leave Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Leave Type <span className="text-red-500">*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand capitalize ${formErrors.type ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              >
                <option value="">Select leave type</option>
                {dynamicLeaveTypes && dynamicLeaveTypes.length > 0 ? (
                  dynamicLeaveTypes.map((type) => (
                    <option key={type._id} value={type.name} className="capitalize">
                      {type.name}
                    </option>
                  ))
                ) : (
                  <option disabled>No active leave types found</option>
                )}
              </select>
              {formErrors.type && (
                <p className="mt-1 text-sm text-red-600">{formErrors.type}</p>
              )}
            </div>

            {/* Annual Entitlement (Days) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Annual Entitlement (Days) <span className="text-red-500">*</span> <span className="text-gray-500 text-xs">(1-180)</span>
              </label>
              <input
                type="number"
                name="entitlement"
                value={formData.entitlement}
                onChange={handleInputChange}
                onKeyDown={handleNumberKeyDown}
                onBlur={handleBlur}
                required
                min="1"
                max="180"
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${formErrors.entitlement ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              />
              <div className="flex justify-between items-center mt-1">
                {formErrors.entitlement && (
                  <p className="text-sm text-red-600">{formErrors.entitlement}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.entitlement || 0}/180 days
                </p>
              </div>
            </div>

            {/* Eligible For */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Eligible For <span className="text-red-500">*</span>
              </label>
              <select
                name="applicableRoles"
                value={formData.applicableRoles}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                className={`w-full px-3 py-2 border rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand capitalize ${formErrors.applicableRoles ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              >
                {roles.map((role) => (
                  <option key={role} value={role} className="capitalize">
                    {role}
                  </option>
                ))}
              </select>
              {formErrors.applicableRoles && (
                <p className="mt-1 text-sm text-red-600">{formErrors.applicableRoles}</p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Policy Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
              placeholder="Detailed explanation of the policy rules..."
            />
          </div>

          {/* Accrual Settings */}
          <div className="mt-8">
            <h3 className="text-md font-semibold text-gray-800 border-b pb-2 mb-4 flex items-center">
              <Clock className="h-4 w-4 mr-2 text-brand" />
              Accrual Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Accrual Frequency</label>
                <select
                  name="accrualFrequency"
                  value={formData.accrualFrequency}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-brand"
                >
                  <option value="none">None (Fixed Quota)</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>
              {formData.accrualFrequency !== 'none' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Accrual Day</label>
                    <select
                      name="accrualDay"
                      value={formData.accrualDay}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-brand"
                    >
                      <option value="first">1st of Cycle</option>
                      <option value="last">Last Day of Cycle</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rate per Cycle (Days)</label>
                    <input
                      type="number"
                      step="0.01"
                      name="accrualRatePerCycle"
                      value={formData.accrualRatePerCycle}
                      onChange={handleInputChange}
                      disabled={true}
                      placeholder="Auto-calculated"
                      className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg outline-none text-gray-500 cursor-not-allowed"
                    />
                  </div>
                </>
              )}
            </div>
          </div>



          {/* Application Rules */}
          <div className="mt-8">
            <h3 className="text-md font-semibold text-gray-800 border-b pb-2 mb-4 flex items-center">
              <FileText className="h-4 w-4 mr-2 text-brand" />
              Application Rules
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Document Proof Required From (Days)</label>
                <input
                  type="number"
                  name="documentProofThreshold"
                  value={formData.documentProofThreshold}
                  onChange={handleInputChange}
                  placeholder="0 = Never required"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-brand"
                />
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <input
                  type="checkbox"
                  name="allowHalfDay"
                  checked={formData.allowHalfDay}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">Allow Half-Day</label>
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <input
                  type="checkbox"
                  name="requireReason"
                  checked={formData.requireReason}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">Mandatory Comment/Reason</label>
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <input
                  type="checkbox"
                  name="enforceRestrictedHolidays"
                  checked={formData.enforceRestrictedHolidays}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">Enforce Restricted Holiday Dates</label>
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <input
                  type="checkbox"
                  name="isUnpaid"
                  checked={formData.isUnpaid}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">Is Unpaid Leave</label>
              </div>
              {formData.enforceRestrictedHolidays && (
                <div className="mt-2 pl-6">
                  <p className="text-xs text-gray-500 italic">
                    Employees can only apply this leave on dates marked as "Restricted" in the Holiday Calendar.
                  </p>

                </div>
              )}
            </div>
          </div>

          {/* Advance Notice Rules */}
          <div className="mt-8">
            <div className="flex items-center justify-between border-b pb-2 mb-4">
              <h3 className="text-md font-semibold text-gray-800 flex items-center">
                <Clock className="h-4 w-4 mr-2 text-brand" />
                Advance Notice Rules
              </h3>
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  advanceNoticeRules: [...prev.advanceNoticeRules, { minLeaveDays: 1, noticeDays: 1 }]
                }))}
                className="text-brand hover:text-brand-dark text-sm font-medium flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Rule
              </button>
            </div>
            {formData.advanceNoticeRules.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No advance notice rules configured. Employees can apply anytime.</p>
            ) : (
              <div className="space-y-3">
                {formData.advanceNoticeRules.map((rule, index) => (
                  <div key={index} className="flex items-center space-x-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="flex-1 grid grid-cols-2 gap-4">
                      <div className="flex items-center">
                        <span className="text-xs text-gray-500 mr-2">If Leave ≥</span>
                        <input
                          type="number"
                          value={rule.minLeaveDays}
                          onChange={(e) => {
                            const newRules = [...formData.advanceNoticeRules];
                            newRules[index].minLeaveDays = parseInt(e.target.value);
                            setFormData(prev => ({ ...prev, advanceNoticeRules: newRules }));
                          }}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <span className="text-xs text-gray-500 ml-2">Days</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-500 mr-2">Need Notice of</span>
                        <input
                          type="number"
                          value={rule.noticeDays}
                          onChange={(e) => {
                            const newRules = [...formData.advanceNoticeRules];
                            newRules[index].noticeDays = parseInt(e.target.value);
                            setFormData(prev => ({ ...prev, advanceNoticeRules: newRules }));
                          }}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <span className="text-xs text-gray-500 ml-2">Days</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newRules = formData.advanceNoticeRules.filter((_, i) => i !== index);
                        setFormData(prev => ({ ...prev, advanceNoticeRules: newRules }));
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sandwich Rule Settings */}
          <div className="mt-8">
            <div className="bg-amber-50 rounded-xl border border-amber-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Sandwich Rule</h3>
                    <p className="text-xs text-amber-700">Deduct weekends/holidays if sandwiched between leaves</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="enabled"
                      checked={formData.sandwichRule?.enabled}
                      onChange={handleSandwichChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
                    <span className="ml-3 text-sm font-bold text-gray-700">{formData.sandwichRule?.enabled ? 'ENABLED' : 'DISABLED'}</span>
                  </label>
                </div>
              </div>

              {formData.sandwichRule?.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-amber-100">
                    <input
                      type="checkbox"
                      name="includeWeekends"
                      checked={formData.sandwichRule?.includeWeekends}
                      onChange={handleSandwichChange}
                      className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                    />
                    <div>
                      <label className="text-sm font-bold text-gray-700 block">Include Weekends</label>
                      <p className="text-[10px] text-gray-500">Treat Saturdays & Sundays as leave if sandwiched</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-amber-100">
                    <input
                      type="checkbox"
                      name="includeHolidays"
                      checked={formData.sandwichRule?.includeHolidays}
                      onChange={handleSandwichChange}
                      className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                    />
                    <div>
                      <label className="text-sm font-bold text-gray-700 block">Include Holidays</label>
                      <p className="text-[10px] text-gray-500">Treat Public Holidays as leave if sandwiched</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-amber-100">
                    <input
                      type="checkbox"
                      name="requireFullDayOnBothSides"
                      checked={formData.sandwichRule?.requireFullDayOnBothSides}
                      onChange={handleSandwichChange}
                      className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                    />
                    <div>
                      <label className="text-sm font-bold text-gray-700 block">Strict Rule</label>
                      <p className="text-[10px] text-gray-500">Requires full day leave on both sides of holiday</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-amber-100">
                    <input
                      type="checkbox"
                      name="treatAbsentAsLeave"
                      checked={formData.sandwichRule?.treatAbsentAsLeave}
                      onChange={handleSandwichChange}
                      className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                    />
                    <div>
                      <label className="text-sm font-bold text-gray-700 block">Treat Absent as Leave</label>
                      <p className="text-[10px] text-gray-500">Sandwich rule triggers even for unapproved absences</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Year-End Policy */}
          <div className="mt-8">
            <h3 className="text-md font-semibold text-gray-800 border-b pb-2 mb-4 flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-brand" />
              Year-End Policy
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Year-End Action</label>
                <select
                  name="yearEndAction"
                  value={formData.yearEndAction}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-brand"
                >
                  <option value="carry_forward">Carry Forward</option>
                  <option value="expire">Expire / Reset</option>
                  <option value="encash">Encash (Manual Process)</option>
                </select>
              </div>
              {formData.yearEndAction === 'carry_forward' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Carry Forward (Days)</label>
                  <input
                    type="number"
                    name="maxCarryForwardDays"
                    value={formData.maxCarryForwardDays}
                    onChange={handleInputChange}
                    placeholder="Unlimited if empty"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:border-brand"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Active/Inactive Toggle */}
          <div className="mt-6">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
              />
              <label className="text-sm font-medium text-gray-700">
                Active Policy
              </label>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={resetForm} aria-label="Cancel policy">
              Cancel policy
            </Button>
            <PermissionGate permission="attendance" action={editingPolicy ? "update" : "create"}>
              <Button
                type="submit"
                variant="primary"
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white border-transparent"
                aria-label={editingPolicy ? "Update leave policy" : "Create leave policy"}
              >
                <Save className="h-4 w-4" />
                <span>{editingPolicy ? "Update leave policy" : "Create leave policy"}</span>
              </Button>
            </PermissionGate>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Leave Policies
            </h2>
          </div>
          <div className="flex items-center space-x-4">
            {selectedPolicyRows.size > 0 && (
              <PermissionGate permission="attendance" action="delete">
                <Button
                  variant="primary"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={handleBulkDeletePolicy}
                  className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
                >
                  Delete ({selectedPolicyRows.size})
                </Button>
              </PermissionGate>
            )}
            <PermissionGate permission="attendance" action="read">
              <div ref={selectedExportWrapRef} className="relative">
                <Button
                  variant="outline"
                  icon={<Download className="h-4 w-4" />}
                  onClick={handleExportClick}
                >
                  Export {selectedPolicyRows.size > 0 ? `(${selectedPolicyRows.size})` : ''}
                </Button>
                {showInlineSelectedFormats && (
                  <SelectedExportFormatBar
                    onFormatSelect={handleSelectedFormatExport}
                    loading={exportLoading}
                    onClose={() => setShowInlineSelectedFormats(false)}
                    onMouseEnter={() => setInlineFormatPanelEntered(true)}
                    onMouseLeave={() => setInlineFormatPanelEntered(false)}
                  />
                )}
              </div>
            </PermissionGate>
            <PermissionGate permission="attendance" action="create">
              <Button
                variant="outline"
                icon={<Upload className="h-4 w-4" />}
                onClick={() => setShowBulkUploadModal(true)}
              >
                Bulk Upload
              </Button>
            </PermissionGate>
            <PermissionGate permission="attendance" action="create">
              <Button
                variant="outline"
                icon={<FileText className="h-4 w-4" />}
                onClick={() => setShowBalanceImportModal(true)}
              >
                Import Balances
              </Button>
            </PermissionGate>
            <PermissionGate permission="attendance" action="create">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowPolicyForm(true)}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white border-transparent"
              >
                <Plus className="h-4 w-4" />
                <span>New Policy</span>
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-end gap-4 w-full">
            {/* Search */}
            <div className="relative flex-[2] min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search policies..."
                value={searchTerm}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                  setSearchTerm(value);
                }}
                onKeyPress={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={100}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    // Maintain focus on search input
                    const searchInput = document.querySelector('input[placeholder="Search policies..."]');
                    if (searchInput) {
                      setTimeout(() => searchInput.focus(), 0);
                    }
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Date Filter */}
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={filterStartDate}
                endDate={filterEndDate}
                allowFutureDates={false}
                onChange={setDateRange}
              />
            </div>

            {/* Eligible For Filter */}
            <div className="flex-shrink-0">
              <select
                value={filterEligibleFor}
                onChange={(e) => setFilterEligibleFor(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                <option value="all">All Eligible For</option>
                <option value="All">All</option>
                <option value="Males">Males</option>
                <option value="Females">Females</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex-shrink-0">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Clear Filters */}
            {(searchTerm ||
              filterEligibleFor !== "all" ||
              filterStatus !== "all" ||
              filterStartDate ||
              filterEndDate) && (
                <div className="flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      resetPolicyFilters();
                    }}
                    className="flex items-center space-x-2 text-gray-600 hover:text-brand border-gray-300 hover:border-brand px-4 h-[42px]"
                  >
                    <RotateCcw className="h-4 w-4" />
                    <span>Clear</span>
                  </Button>
                </div>
              )}
          </div>
        </div >

        <>

          {/* Policies Table */}
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                <tr>
                  <th className="px-4 py-3 w-12">
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded cursor-pointer"
                        checked={visiblePolicies.length > 0 && visiblePolicies.every(p => selectedPolicyRows.has(p.id))}
                        onChange={handleSelectAllPolicies}
                      />
                    </div>
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Policy ID
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Policy Name</span>
                      {sortField === 'name' ? (
                        sortOrder === 'asc' ?
                          <ArrowUp className="h-3 w-3 text-brand" /> :
                          <ArrowDown className="h-3 w-3 text-brand" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider w-32 cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Leave Type</span>
                      {sortField === 'type' ? (
                        sortOrder === 'asc' ?
                          <ArrowUp className="h-3 w-3 text-brand" /> :
                          <ArrowDown className="h-3 w-3 text-brand" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('entitlement')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Entitlement</span>
                      {sortField === 'entitlement' ? (
                        sortOrder === 'asc' ?
                          <ArrowUp className="h-3 w-3 text-brand" /> :
                          <ArrowDown className="h-3 w-3 text-brand" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('eligibleFor')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Eligible For</span>
                      {sortField === 'eligibleFor' ? (
                        sortOrder === 'asc' ?
                          <ArrowUp className="h-3 w-3 text-brand" /> :
                          <ArrowDown className="h-3 w-3 text-brand" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortField === 'status' ? (
                        sortOrder === 'asc' ?
                          <ArrowUp className="h-3 w-3 text-brand" /> :
                          <ArrowDown className="h-3 w-3 text-brand" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created At</span>
                      {sortField === 'createdAt' ? (
                        sortOrder === 'asc' ?
                          <ArrowUp className="h-3 w-3 text-brand" /> :
                          <ArrowDown className="h-3 w-3 text-brand" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-gray-900 uppercase tracking-wider text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visiblePolicies.length > 0 ? (
                  visiblePolicies.map((policy) => (
                    <tr key={policy.id} className={`hover:bg-blue-50/30 ${selectedPolicyRows.has(policy.id) ? 'bg-brand/5' : ''}`}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded cursor-pointer"
                            checked={selectedPolicyRows.has(policy.id)}
                            onChange={() => handlePolicyRowSelect(policy.id)}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-700 font-semibold">
                        {policy.policyId || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap overflow-hidden">
                        <div className="text-sm font-medium text-gray-900 truncate capitalize max-w-[320px]">
                          {policy.name}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 truncate capitalize overflow-hidden max-w-[200px]">
                        {policy.type}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {policy.entitlement} days
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {policy.applicableRoles && Array.isArray(policy.applicableRoles) ? (
                            policy.applicableRoles.map((role, idx) => (
                              <span
                                key={`${role}-${idx}`}
                                className="inline-flex px-2 py-1 text-xs bg-brand/10 text-brand rounded-full truncate capitalize"
                              >
                                {role}
                              </span>
                            ))
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs bg-brand/10 text-brand rounded-full truncate capitalize">
                              {policy.applicableRoles || 'All'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${policy.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}
                        >
                          {policy.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {policy.createdAt ? formatDate(policy.createdAt, { includeTime: true }) : "N/A"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <PermissionGate permission="attendance" action="read">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openPolicyDetails(policy)}
                              className="text-brand hover:text-brand-dark"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="attendance" action="update">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditPolicy(policy)}
                              className="text-brand hover:text-brand-dark"
                              title="Edit Policy"
                              aria-label="Edit leave policy"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="attendance" action="update">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => togglePolicyStatus(policy.id)}
                              className={`${policy.isActive
                                ? 'text-red-600 hover:text-red-900'
                                : 'text-brand hover:text-brand-dark'
                                }`}
                              title={policy.isActive ? 'Deactivate Policy' : 'Activate Policy'}
                              aria-label={policy.isActive ? 'Deactivate leave policy' : 'Activate leave policy'}
                            >
                              {policy.isActive ? (
                                <XCircle className="h-4 w-4" />
                              ) : (
                                <CheckCircle className="h-4 w-4" />
                              )}
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="attendance" action="delete">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeletePolicy(policy.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Policy (Permanent)"
                              aria-label="Delete leave policy permanently"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : !isLoadingPolicies && !isSearchingPolicies ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-6 text-center text-sm text-gray-500">
                      No policies found. Try adjusting filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            {isLoadingPolicies && (
              <div className="py-12 flex items-center justify-center space-x-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand"></div>
                <span>{isSearchingPolicies ? 'Searching...' : 'Loading leave policies...'}</span>
              </div>
            )}
            {
              shouldShowPolicyPagination ? (
                  <PaginationControls
                    currentPage={currentPolicyPage}
                    totalPages={totalPolicyPages}
                    totalItems={totalPolicies}
                    limit={itemsPerPagePolicies}
                    hasNextPage={hasNextPolicyPage}
                    hasPrevPage={hasPrevPolicyPage}
                    onPageChange={handlePolicyPageChange}
                    onLimitChange={handlePolicyItemsPerPageChange}
                    limitOptions={[10, 20, 25, 50, 100]}
                    border={false}
                  />
              ) : null
            }
          </div>

          {filteredPolicies.length === 0 && !isLoadingPolicies && !isSearchingPolicies && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No policies found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search criteria or create a new policy.
              </p>
            </div>
          )}
        </>

        {/* Custom Delete Confirmation Dialog */}
        <WarningPopup
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setPendingAction(null);
            setIsBulkDelete(false);
          }}
          onConfirm={confirmDelete}
          title={isBulkDelete ? "Delete Policies" : "Delete Policy"}
          message={isBulkDelete
            ? `Are you sure you want to delete ${selectedPolicyRows.size} selected policies? This action cannot be undone.`
            : `Are you sure you want to delete the policy "${pendingAction?.policy?.name}"? This action cannot be undone.`
          }
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="danger"
        />

        {
          showToggleConfirm && pendingAction && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] !mt-0">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
                <div className="flex items-center mb-4 text-brand">
                  <AlertCircle className="h-6 w-6 mr-2" />
                  <h3 className="text-xl font-bold">Update Status</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to {pendingAction.policy.isActive ? "deactivate" : "activate"} policy "
                  <span className="font-semibold text-gray-900">{pendingAction.policy.name}</span>"?
                </p>
                <div className="flex justify-end space-x-3">
                  <Button variant="secondary" onClick={cancelToggle}>Cancel</Button>
                  <Button
                    onClick={confirmToggle}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          )
        }

        {/* Policy Details Modal */}
        {
          showPolicyDetails && selectedPolicyDetails && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
              <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto transform transition-all animate-in zoom-in-95 duration-300">
                {/* Header */}
                <div className="relative px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-brand/5 to-transparent">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-brand/10 rounded-lg">
                        <FileText className="w-6 h-6 text-brand" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          Policy Details
                        </h3>
                        <p className="text-sm text-gray-500">
                          Detailed information about the leave policy
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={closePolicyDetails}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-blue-100/60 rounded-full transition-all"
                      title="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-8">
                  {/* Policy Name Section - Primary Info */}
                  <div className="bg-gray-50/50 border border-gray-100 rounded-xl p-5 hover:bg-blue-50/30 transition-colors">
                    <div className="flex items-center space-x-2 text-gray-500 mb-2">
                      <FileText className="w-4 h-4" />
                      <span className="text-xs font-semibold uppercase tracking-wider">Policy Name</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 break-words capitalize">
                      {selectedPolicyDetails.name || "N/A"}
                    </p>
                  </div>

                  {/* Grid for Quick Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-1.5">
                      <div className="flex items-center space-x-2 text-gray-500 mb-1">
                        <Calendar className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Leave Type</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-base font-medium text-gray-900 border-l-4 border-brand pl-3 capitalize">
                          {selectedPolicyDetails.type || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center space-x-2 text-gray-500 mb-1">
                        <Clock className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Entitlement</span>
                      </div>
                      <p className="text-base font-medium text-gray-900 border-l-4 border-brand pl-3">
                        {selectedPolicyDetails.entitlement ? `${selectedPolicyDetails.entitlement} days` : "N/A"}
                        <span className="text-gray-400 text-xs ml-1 font-normal">/ year</span>
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center space-x-2 text-gray-500 mb-1">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Current Status</span>
                      </div>
                      <div className="pl-3 border-l-4 border-gray-200">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide ${selectedPolicyDetails.isActive
                            ? 'bg-brand/10 text-brand'
                            : 'bg-red-100 text-red-700'
                            }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full mr-2 ${selectedPolicyDetails.isActive ? 'bg-brand' : 'bg-red-500'}`} />
                          {selectedPolicyDetails.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Roles Section */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-gray-500">
                      <User className="w-4 h-4" />
                      <span className="text-xs font-semibold uppercase tracking-wider">Eligible For</span>
                    </div>
                    <div className="bg-gray-50/50 border border-gray-100 rounded-xl p-5">
                      <div className="flex flex-wrap gap-2.5">
                        {selectedPolicyDetails.applicableRoles && Array.isArray(selectedPolicyDetails.applicableRoles) ? (
                          selectedPolicyDetails.applicableRoles.map((role) => (
                            <span
                              key={role}
                              className="inline-flex px-4 py-1.5 text-sm font-medium bg-white border border-brand/20 text-brand rounded-lg shadow-sm capitalize hover:border-brand/40 transition-colors"
                            >
                              {role}
                            </span>
                          ))
                        ) : (
                          <span className="inline-flex px-4 py-1.5 text-sm font-medium bg-white border border-brand/20 text-brand rounded-lg shadow-sm capitalize">
                            {selectedPolicyDetails.applicableRoles || 'All'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description Section */}
                  {selectedPolicyDetails.description && (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2 text-gray-500">
                        <FileText className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Policy Description</span>
                      </div>
                      <div className="bg-gray-50/50 border border-gray-100 rounded-xl p-5 italic text-gray-700 text-sm">
                        {selectedPolicyDetails.description}
                      </div>
                    </div>
                  )}

                  {/* Detailed Rules Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Accrual Rules */}
                    <div className="space-y-4">
                      <h4 className="font-bold text-gray-900 border-l-4 border-brand pl-3 flex items-center">
                        <Clock className="w-4 h-4 mr-2 text-brand" />
                        Accrual Rules
                      </h4>
                      <div className="space-y-3 pl-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Frequency:</span>
                          <span className="font-semibold capitalize">{selectedPolicyDetails.accrualFrequency || 'Fixed Quota'}</span>
                        </div>
                        {selectedPolicyDetails.accrualFrequency !== 'none' && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Accrual Day:</span>
                              <span className="font-semibold capitalize">{selectedPolicyDetails.accrualDay || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Rate per Cycle:</span>
                              <span className="font-semibold text-brand">{selectedPolicyDetails.accrualRatePerCycle || 0} days</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>


                  </div>

                  {/* Application & Year End */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <h4 className="font-bold text-gray-900 border-l-4 border-brand pl-3 flex items-center">
                        <Save className="w-4 h-4 mr-2 text-brand" />
                        Application Rules
                      </h4>
                      <div className="space-y-3 pl-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Half-Day:</span>
                          <span className={selectedPolicyDetails.allowHalfDay ? 'text-green-600 font-bold' : 'text-red-600'}>
                            {selectedPolicyDetails.allowHalfDay ? 'Allowed' : 'Restricted'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Reason:</span>
                          <span className={selectedPolicyDetails.requireReason ? 'text-gray-900 font-semibold' : 'text-gray-400'}>
                            {selectedPolicyDetails.requireReason ? 'Mandatory' : 'Optional'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Proof Required:</span>
                          <span className="font-semibold text-brand">
                            {selectedPolicyDetails.documentProofThreshold > 0
                              ? `If ≥ ${selectedPolicyDetails.documentProofThreshold} days`
                              : 'Not Required'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Restricted Date Check:</span>
                          <span className={selectedPolicyDetails.enforceRestrictedHolidays ? 'text-brand font-bold' : 'text-gray-400'}>
                            {selectedPolicyDetails.enforceRestrictedHolidays ? 'Enforced' : 'No'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Unpaid Leave:</span>
                          <span className={selectedPolicyDetails.isUnpaid ? 'text-red-600 font-bold' : 'text-green-600'}>
                            {selectedPolicyDetails.isUnpaid ? 'Yes (Salary Deduction)' : 'No (Paid Leave)'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-bold text-gray-900 border-l-4 border-brand pl-3 flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-brand" />
                        Year-End Policy
                      </h4>
                      <div className="space-y-3 pl-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Action:</span>
                          <span className="font-semibold capitalize text-brand">{(selectedPolicyDetails.yearEndAction || 'expire').replace('_', ' ')}</span>
                        </div>
                        {selectedPolicyDetails.yearEndAction === 'carry_forward' && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Max Carry Forward:</span>
                            <span className="font-semibold">{selectedPolicyDetails.maxCarryForwardDays || 'Unlimited'} days</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Advance Notice Rules Detail */}
                  {selectedPolicyDetails.advanceNoticeRules && selectedPolicyDetails.advanceNoticeRules.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-bold text-gray-900 border-l-4 border-brand pl-3 flex items-center">
                        <Clock className="w-4 h-4 mr-2 text-brand" />
                        Advance Notice Tiered Rules
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-4">
                        {selectedPolicyDetails.advanceNoticeRules.map((rule, idx) => (
                          <div key={idx} className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex justify-between text-sm">
                            <span className="text-gray-600">If Leave ≥ <span className="font-bold text-gray-900">{rule.minLeaveDays}</span> days</span>
                            <span className="text-brand font-bold">{rule.noticeDays} days notice</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* System Info */}
                  <div className="bg-brand/5 border border-brand/20 rounded-xl p-5 mt-4 flex justify-between items-center">
                    <div>
                      <div className="flex items-center space-x-2 text-gray-500 mb-1">
                        <Clock className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Policy Created</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedPolicyDetails.createdAt
                          ? formatDate(selectedPolicyDetails.createdAt, { includeTime: true })
                          : (selectedPolicyDetails.createdDate ? selectedPolicyDetails.createdDate : "N/A")}
                      </p>
                    </div>
                    {selectedPolicyDetails.lastModified && (
                      <div className="text-right">
                        <div className="flex items-center justify-end space-x-2 text-gray-500 mb-1">
                          <span className="text-xs font-semibold uppercase tracking-wider">Last Modified</span>
                        </div>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(selectedPolicyDetails.lastModified, { includeTime: false })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-5 bg-gray-50/80 border-t border-gray-100 flex justify-end">
                  <Button
                    variant="secondary"
                    onClick={closePolicyDetails}
                    className="px-6 py-2.5 font-semibold text-gray-700 hover:bg-gray-400 focus:ring-gray-300"
                  >
                    Close View
                  </Button>
                </div>
              </div>
            </div>
          )
        }

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          scopedFormatExport={{
            onExport: handleScopedExport,
          }}
          loading={exportLoading}
          title="Export Leave Policies"
          showDateRange={true}
        />
      </div>
      {showBulkUploadModal && (
        <BulkUploadModal
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => {
            loadPolicies(debouncedSearchTerm, filterEligibleFor, filterStatus, filterStartDate, filterEndDate);
            setShowBulkUploadModal(false);
          }}
          config={{
            title: 'Bulk Upload Leave Policies',
            subtitle: 'Import multiple leave policies using a CSV file',
            templateEndpoint: '/leave-policies/bulk-upload/template',
            validateEndpoint: '/leave-policies/bulk-upload/validate',
            uploadEndpoint: '/leave-policies/bulk-upload',
            templateFilename: 'leave_policy_template.csv',
            dataKey: 'data',
            instructions: [
              'Fill in policy details in the CSV',
              'Required fields: Policy Name, Leave Type, Annual Entitlement (Days), Eligible For',
              'Eligible For options: All, Males, Females'
            ]
          }}
        />
      )}
      <LeaveBalanceImportModal
        isOpen={showBalanceImportModal}
        onClose={() => setShowBalanceImportModal(false)}
        onSuccess={() => {
          loadPolicies(debouncedSearchTerm, filterEligibleFor, filterStatus, filterStartDate, filterEndDate);
          setShowBalanceImportModal(false);
        }}
      />
    </div>
  );
};

export default LeavePolicies;
